/**
 * Saved progress: three profile slots in localStorage.
 *
 * Pure logic: no Phaser imports. Storage is injected so tests pass a fake, and
 * every access is wrapped - a blocked or full localStorage drops the game into an
 * in-memory save rather than breaking play (CLAUDE.md).
 */
import {
  currentAnimalId,
  DEFAULT_ANIMAL_ID,
  isAnimalId,
  unlockedAnimalsFor,
  type AnimalId,
} from '../data/animals';
import { deflateSync, inflateSync } from 'fflate';
import { earnedCosmetics, isCosmeticId } from './TideRun';
import type { CosmeticId, TideLevelClear, TideProgress, TideRunSummary } from './TideRun';
import type { GameRunStats, GradeBand } from '../types';
import { GRADE_BANDS } from './WordPicker';

export const STORAGE_KEY = 'questjump.v1';
export const SAVE_VERSION = 3;
export const PROFILE_SLOTS = 3;
export const MAX_NAME_LENGTH = 12;

export interface LevelRecord {
  completed: boolean;
  bestStars: number;
  bestScore: number;
  bestTimeSec: number;
  bestWpm: number;
  bestAccuracy: number;
  /** Fastest Tide Run clear of this level, in seconds; 0 until there is one. Added in v3. */
  tideBestTimeSec: number;
  /** Best tide stars for this level, 0-3. Added in v3. */
  tideStars: number;
}

/** Tide Run records across every run. Added in v3. */
export interface TideRecords {
  runs: number;
  /** Highest level any run has reached. */
  furthestLevel: number;
  mostLevelsInRun: number;
  bestRunWpm: number;
}

/** One finished run, kept so the grown-up screen can chart progress. */
export interface RunEntry {
  /** When the run ended, ISO 8601. */
  at: string;
  level: number;
  wpm: number;
  accuracy: number;
  stars: number;
}

/**
 * How many runs a profile remembers.
 *
 * Enough to show months of progress at a few runs a day, and small enough that
 * three full profiles stay a few tens of KB of localStorage.
 */
export const RUN_HISTORY_LIMIT = 200;

/**
 * How many distinct missed words a profile remembers.
 *
 * The grown-up screen shows the top 20, so this only has to be large enough
 * that a word does not fall off the list just before it would have mattered.
 * The least-missed are dropped first.
 */
export const MISSED_WORDS_LIMIT = 300;

export interface ProfileSettings {
  musicMuted: boolean;
  sfxMuted: boolean;
  reducedMotion: boolean;
  keyboardHints: boolean;
  /**
   * Show every word in the level down the left, for reading ahead. Off by
   * default; a save from before it existed reads as off, which is also right.
   */
  wordList: boolean;
  /**
   * Submit the word the moment it is typed correctly, with no Space or Enter.
   * On by default, and a save from before it existed reads as on too: it is
   * the faster way to play, and a wrong word still needs a key to submit.
   */
  autoEnter: boolean;
}

export interface ProfileTotals {
  stars: number;
  score: number;
  wordsTyped: number;
  levelsCompleted: number;
  playMs: number;
}

export interface ProfileSlot {
  name: string;
  createdAt: string;
  selectedAnimal: AnimalId;
  /** Swatch index per animal; absent means natural colours. */
  colorPerAnimal: Partial<Record<AnimalId, number>>;
  /** Kept for the deferred timed mode, so enabling it needs no migration. */
  timedMode: boolean;
  settings: ProfileSettings;
  levels: Record<number, LevelRecord>;
  totals: ProfileTotals;
  unlockedAnimals: AnimalId[];
  /** Most recent runs, oldest first, capped at RUN_HISTORY_LIMIT. Added in v2. */
  history: RunEntry[];
  /** Times each word was missed. Added in v2. */
  missedWords: Record<string, number>;
  /** Times each expected key was mistyped. Added in v2. */
  missedKeys: Record<string, number>;
  /** Added in v3. */
  tide: TideRecords;
  /** Rewards earned in Tide Run, such as the extra colour swatches. Added in v3. */
  unlockedCosmetics: CosmeticId[];
  /**
   * Has seen the "Pick your helpers" page. New players see it after naming
   * themselves; players from before it existed see it once on the map. Saves
   * without the field read as false, which is exactly that.
   */
  seenHelpers: boolean;
  /**
   * The player's school grade, which gives them their own word ramp; null is
   * "not sure", the original ramp. Never changes which levels are open.
   */
  grade: GradeBand | null;
}

export interface SaveFile {
  version: number;
  profiles: (ProfileSlot | null)[];
  activeSlot: number | null;
}

/** The slice of the Storage API we need, so tests can supply a fake. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Each step upgrades a save FROM the version it is keyed by.
 *
 * v1 -> v2 adds the run history and miss counts the grown-up screen needs.
 * coerceProfile would default those fields on its own, so this step is not
 * strictly load-bearing; it exists so the upgrade is explicit, visible in one
 * place, and covered by a test rather than relying on a parser's leniency.
 */
export const MIGRATIONS: Record<number, (raw: Record<string, unknown>) => Record<string, unknown>> =
  {
    1: (raw) => {
      const profiles = Array.isArray(raw.profiles) ? raw.profiles : [];
      return {
        ...raw,
        version: 2,
        profiles: profiles.map((profile: unknown) => {
          if (typeof profile !== 'object' || profile === null) return profile;
          return { history: [], missedWords: {}, missedKeys: {}, ...profile };
        }),
      };
    },
    // v2 -> v3 adds Tide Run: records, cosmetics, and a tide time and stars on
    // every level. Existing saves start with no tide data at all.
    2: (raw) => {
      const profiles = Array.isArray(raw.profiles) ? raw.profiles : [];
      return {
        ...raw,
        version: 3,
        profiles: profiles.map((profile: unknown) => {
          const slot = asRecord(profile);
          if (slot === null) return profile;
          const levels: Record<string, unknown> = {};
          for (const [key, record] of Object.entries(asRecord(slot.levels) ?? {})) {
            levels[key] = { tideBestTimeSec: 0, tideStars: 0, ...(asRecord(record) ?? {}) };
          }
          return { tide: defaultTideRecords(), unlockedCosmetics: [], ...slot, levels };
        }),
      };
    },
  };

export function defaultTideRecords(): TideRecords {
  return { runs: 0, furthestLevel: 0, mostLevelsInRun: 0, bestRunWpm: 0 };
}

export function defaultSettings(): ProfileSettings {
  return {
    musicMuted: false,
    sfxMuted: false,
    reducedMotion: false,
    keyboardHints: false,
    wordList: false,
    autoEnter: true,
  };
}

export function defaultTotals(): ProfileTotals {
  return { stars: 0, score: 0, wordsTyped: 0, levelsCompleted: 0, playMs: 0 };
}

export function emptyLevelRecord(): LevelRecord {
  return {
    completed: false,
    bestStars: 0,
    bestScore: 0,
    bestTimeSec: 0,
    bestWpm: 0,
    bestAccuracy: 0,
    tideBestTimeSec: 0,
    tideStars: 0,
  };
}

export function createProfile(name: string, now = new Date()): ProfileSlot {
  return {
    name: sanitizeName(name),
    createdAt: now.toISOString(),
    selectedAnimal: DEFAULT_ANIMAL_ID,
    colorPerAnimal: {},
    timedMode: false,
    settings: defaultSettings(),
    levels: {},
    totals: defaultTotals(),
    unlockedAnimals: unlockedAnimalsFor(0, 0),
    history: [],
    missedWords: {},
    missedKeys: {},
    tide: defaultTideRecords(),
    unlockedCosmetics: [],
    seenHelpers: false,
    grade: null,
  };
}

export function emptySaveFile(): SaveFile {
  return { version: SAVE_VERSION, profiles: [null, null, null], activeSlot: null };
}

/** Letters and single spaces, trimmed, capped. Keeps a name typeable and safe. */
export function sanitizeName(raw: string): string {
  return raw
    .replace(/[^a-zA-Z ]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_NAME_LENGTH);
}

export function isValidName(raw: string): boolean {
  return sanitizeName(raw).length > 0;
}

/* --------------------------------------------------------------- save codes */

/** FNV-1a over the payload, so a mistyped save code is rejected rather than loaded. */
export function checksum(text: string): string {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function base64ToBytes(text: string): Uint8Array {
  const binary = atob(text);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Save codes are "QJ2." + the save, checksummed, compressed and written in
 * URL-safe base64: about a tenth the length of the plain JSON. One player on
 * their own, small enough for a QR code, is profileCode.ts.
 */
export const SAVE_CODE_PREFIX = 'QJ2.';

function toBase64Url(bytes: Uint8Array): string {
  return bytesToBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(text: string): Uint8Array {
  const plain = text.replace(/-/g, '+').replace(/_/g, '/');
  return base64ToBytes(plain + '='.repeat((4 - (plain.length % 4)) % 4));
}

/** UTF-8 then compressed, so an accented profile name cannot corrupt the code. */
export function encodeSaveCode(file: SaveFile): string {
  const data = JSON.stringify(file);
  const envelope = JSON.stringify({ v: SAVE_VERSION, sum: checksum(data), data });
  return (
    SAVE_CODE_PREFIX + toBase64Url(deflateSync(new TextEncoder().encode(envelope), { level: 9 }))
  );
}

/** Pull the code out of whatever was pasted or scanned: a code, or a #save= link. */
export function extractSaveCode(text: string): string {
  const trimmed = text.trim();
  const marker = trimmed.indexOf('#save=');
  return marker >= 0 ? decodeURIComponent(trimmed.slice(marker + 6)) : trimmed;
}

export type ImportResult =
  | { ok: true; file: SaveFile }
  | { ok: false; reason: 'unreadable' | 'checksum' | 'version' | 'shape' };

export function decodeSaveCode(input: string): ImportResult {
  const code = extractSaveCode(input);
  let envelope: unknown;
  try {
    // Codes from before compression are plain base64 JSON, and still load.
    const bytes = code.startsWith(SAVE_CODE_PREFIX)
      ? inflateSync(fromBase64Url(code.slice(SAVE_CODE_PREFIX.length)))
      : base64ToBytes(code);
    envelope = JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return { ok: false, reason: 'unreadable' };
  }

  if (typeof envelope !== 'object' || envelope === null) return { ok: false, reason: 'shape' };
  const { v, sum, data } = envelope as { v?: unknown; sum?: unknown; data?: unknown };
  if (typeof data !== 'string' || typeof sum !== 'string') return { ok: false, reason: 'shape' };
  if (typeof v !== 'number' || v > SAVE_VERSION) return { ok: false, reason: 'version' };
  if (checksum(data) !== sum) return { ok: false, reason: 'checksum' };

  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return { ok: false, reason: 'unreadable' };
  }
  const file = coerceSaveFile(parsed);
  return file === null ? { ok: false, reason: 'shape' } : { ok: true, file };
}

/* ------------------------------------------------------------------ parsing */

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
}

function num(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function bool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function coerceLevelRecord(value: unknown): LevelRecord {
  const raw = asRecord(value);
  if (raw === null) return emptyLevelRecord();
  return {
    completed: bool(raw.completed),
    bestStars: num(raw.bestStars),
    bestScore: num(raw.bestScore),
    bestTimeSec: num(raw.bestTimeSec),
    bestWpm: num(raw.bestWpm),
    bestAccuracy: num(raw.bestAccuracy),
    tideBestTimeSec: num(raw.tideBestTimeSec),
    tideStars: Math.min(3, Math.max(0, num(raw.tideStars))),
  };
}

function coerceTideRecords(value: unknown): TideRecords {
  const raw = asRecord(value) ?? {};
  return {
    runs: num(raw.runs),
    furthestLevel: num(raw.furthestLevel),
    mostLevelsInRun: num(raw.mostLevelsInRun),
    bestRunWpm: num(raw.bestRunWpm),
  };
}

function coerceRunEntry(value: unknown): RunEntry | null {
  const raw = asRecord(value);
  if (raw === null) return null;
  const level = num(raw.level);
  if (level < 1) return null;
  return {
    at: typeof raw.at === 'string' ? raw.at : new Date(0).toISOString(),
    level,
    wpm: num(raw.wpm),
    accuracy: num(raw.accuracy),
    stars: num(raw.stars),
  };
}

/** A word or key -> count map, with anything that is not a positive count dropped. */
function coerceCounts(value: unknown): Record<string, number> {
  const raw = asRecord(value) ?? {};
  const out: Record<string, number> = {};
  for (const [key, count] of Object.entries(raw)) {
    if (key !== '' && typeof count === 'number' && Number.isFinite(count) && count > 0) {
      out[key] = Math.floor(count);
    }
  }
  return out;
}

export function coerceProfile(value: unknown): ProfileSlot | null {
  const raw = asRecord(value);
  if (raw === null) return null;
  const name = sanitizeName(typeof raw.name === 'string' ? raw.name : '');
  if (name === '') return null;

  const settingsRaw = asRecord(raw.settings) ?? {};
  const totalsRaw = asRecord(raw.totals) ?? {};
  const levelsRaw = asRecord(raw.levels) ?? {};
  const colorsRaw = asRecord(raw.colorPerAnimal) ?? {};

  const levels: Record<number, LevelRecord> = {};
  for (const [key, record] of Object.entries(levelsRaw)) {
    const level = Number.parseInt(key, 10);
    if (Number.isFinite(level)) levels[level] = coerceLevelRecord(record);
  }

  const colorPerAnimal: Partial<Record<AnimalId, number>> = {};
  for (const [rawKey, index] of Object.entries(colorsRaw)) {
    const key = currentAnimalId(rawKey);
    if (isAnimalId(key) && typeof index === 'number') colorPerAnimal[key] = index;
  }

  const selected =
    typeof raw.selectedAnimal === 'string' ? currentAnimalId(raw.selectedAnimal) : '';

  return {
    name,
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : new Date(0).toISOString(),
    selectedAnimal: isAnimalId(selected) ? selected : DEFAULT_ANIMAL_ID,
    colorPerAnimal,
    timedMode: bool(raw.timedMode),
    settings: {
      musicMuted: bool(settingsRaw.musicMuted),
      sfxMuted: bool(settingsRaw.sfxMuted),
      reducedMotion: bool(settingsRaw.reducedMotion),
      keyboardHints: bool(settingsRaw.keyboardHints),
      wordList: bool(settingsRaw.wordList),
      autoEnter: settingsRaw.autoEnter === undefined ? true : bool(settingsRaw.autoEnter),
    },
    levels,
    totals: {
      stars: num(totalsRaw.stars),
      score: num(totalsRaw.score),
      wordsTyped: num(totalsRaw.wordsTyped),
      levelsCompleted: num(totalsRaw.levelsCompleted),
      playMs: num(totalsRaw.playMs),
    },
    unlockedAnimals: Array.isArray(raw.unlockedAnimals)
      ? raw.unlockedAnimals
          .map((id) => (typeof id === 'string' ? currentAnimalId(id) : id))
          .filter((id): id is AnimalId => typeof id === 'string' && isAnimalId(id))
      : [],
    history: Array.isArray(raw.history)
      ? raw.history
          .map(coerceRunEntry)
          .filter((entry): entry is RunEntry => entry !== null)
          .slice(-RUN_HISTORY_LIMIT)
      : [],
    missedWords: coerceCounts(raw.missedWords),
    missedKeys: coerceCounts(raw.missedKeys),
    tide: coerceTideRecords(raw.tide),
    unlockedCosmetics: Array.isArray(raw.unlockedCosmetics)
      ? raw.unlockedCosmetics.filter(
          (id): id is CosmeticId => typeof id === 'string' && isCosmeticId(id),
        )
      : [],
    seenHelpers: bool(raw.seenHelpers),
    grade: coerceGrade(raw.grade),
  };
}

function coerceGrade(value: unknown): GradeBand | null {
  return typeof value === 'number' && (GRADE_BANDS as readonly number[]).includes(value)
    ? (value as GradeBand)
    : null;
}

/** Anything unrecognisable becomes a fresh file rather than an exception. */
export function coerceSaveFile(value: unknown): SaveFile | null {
  let raw = asRecord(value);
  if (raw === null) return null;

  // Walk any registered migrations up to the current version.
  let version = num(raw.version, SAVE_VERSION);
  while (version < SAVE_VERSION) {
    const step = MIGRATIONS[version];
    if (step === undefined) break;
    raw = step(raw);
    version = num(raw.version, version + 1);
  }

  const profilesRaw = Array.isArray(raw.profiles) ? raw.profiles : [];
  const profiles: (ProfileSlot | null)[] = [];
  for (let slot = 0; slot < PROFILE_SLOTS; slot += 1) {
    profiles.push(coerceProfile(profilesRaw[slot]));
  }

  const active = raw.activeSlot;
  const candidate = typeof active === 'number' ? profiles[active] : undefined;
  const activeSlot =
    typeof active === 'number' && candidate !== undefined && candidate !== null ? active : null;

  return { version: SAVE_VERSION, profiles, activeSlot };
}

/**
 * Keep the `limit` highest counts, dropping the least-missed first.
 *
 * Ties go to the alphabetically earlier word, so trimming is deterministic and
 * a save never changes just because it was trimmed twice.
 */
export function trimCounts(counts: Record<string, number>, limit: number): void {
  const keys = Object.keys(counts);
  if (keys.length <= limit) return;
  const keep = new Set(
    keys.sort((a, b) => (counts[b] ?? 0) - (counts[a] ?? 0) || a.localeCompare(b)).slice(0, limit),
  );
  for (const key of keys) if (!keep.has(key)) delete counts[key];
}

/* ------------------------------------------------------------------ manager */

export interface RecordRunResult {
  readonly newUnlocks: AnimalId[];
  /** Best score before this run, so Results can show "best before". */
  readonly previousBestScore: number;
  readonly improved: boolean;
}

export type TideRecordName = 'furthest' | 'most' | 'wpm';

export interface TideRunResult {
  /** Records this run beat. Empty on a first run, which has nothing to beat. */
  readonly newRecords: TideRecordName[];
  readonly newCosmetics: CosmeticId[];
  readonly newUnlocks: AnimalId[];
  /** Tide stars across every level after this run: the best on each counts. */
  readonly tideStarsTotal: number;
}

export interface TideClearResult {
  readonly clear: TideLevelClear;
  /** Rewards this clear earned, granted on the spot. */
  readonly newCosmetics: CosmeticId[];
  readonly newUnlocks: AnimalId[];
}

/** The run's typing that feeds the grown-up screen's miss counts. */
export interface TideRunTyping {
  readonly wordsCorrect: number;
  readonly missedWords: readonly string[];
  readonly missedLetters: Readonly<Record<string, number>>;
}

function tideStarsOf(profile: ProfileSlot): number {
  return Object.values(profile.levels).reduce((sum, record) => sum + record.tideStars, 0);
}

export class SaveManager {
  private file: SaveFile = emptySaveFile();
  private memoryOnly = false;
  private fallback = new Map<string, string>();

  constructor(private readonly storage: StorageLike | null) {
    this.load();
  }

  /** False when storage is blocked and progress lives only in memory. */
  get storageAvailable(): boolean {
    return !this.memoryOnly;
  }

  get data(): SaveFile {
    return this.file;
  }

  load(): SaveFile {
    const raw = this.read(STORAGE_KEY);
    if (raw === null) {
      this.file = emptySaveFile();
      return this.file;
    }
    try {
      this.file = coerceSaveFile(JSON.parse(raw)) ?? emptySaveFile();
    } catch {
      // Corrupt save: start clean rather than refusing to run.
      this.file = emptySaveFile();
    }
    return this.file;
  }

  save(): void {
    this.write(STORAGE_KEY, JSON.stringify(this.file));
  }

  /* ----------------------------------------------------------- profiles */

  profiles(): readonly (ProfileSlot | null)[] {
    return this.file.profiles;
  }

  activeProfile(): ProfileSlot | null {
    const slot = this.file.activeSlot;
    return slot === null ? null : (this.file.profiles[slot] ?? null);
  }

  activeSlot(): number | null {
    return this.file.activeSlot;
  }

  addProfile(slot: number, name: string): ProfileSlot | null {
    if (slot < 0 || slot >= PROFILE_SLOTS || !isValidName(name)) return null;
    const profile = createProfile(name);
    this.file.profiles[slot] = profile;
    this.file.activeSlot = slot;
    this.save();
    return profile;
  }

  deleteProfile(slot: number): void {
    if (slot < 0 || slot >= PROFILE_SLOTS) return;
    this.file.profiles[slot] = null;
    if (this.file.activeSlot === slot) this.file.activeSlot = null;
    this.save();
  }

  /**
   * Where a player brought in from a QR code or save picture would go: over
   * the player with the same name if there is one, otherwise the first free
   * slot. Null when every slot holds someone else.
   */
  profileImportSlot(name: string): { slot: number; replacing: boolean } | null {
    const wanted = name.trim().toLowerCase();
    const same = this.file.profiles.findIndex(
      (profile) => profile !== null && profile.name.trim().toLowerCase() === wanted,
    );
    if (same >= 0) return { slot: same, replacing: true };
    const free = this.file.profiles.findIndex((profile) => profile === null);
    return free >= 0 ? { slot: free, replacing: false } : null;
  }

  /** Put one player into a slot and make them the active player. */
  importProfile(profile: ProfileSlot, slot: number): void {
    if (slot < 0 || slot >= PROFILE_SLOTS) return;
    this.file.profiles[slot] = profile;
    this.file.activeSlot = slot;
    this.save();
  }

  selectSlot(slot: number): ProfileSlot | null {
    const profile = this.file.profiles[slot] ?? null;
    if (profile === null) return null;
    this.file.activeSlot = slot;
    this.save();
    return profile;
  }

  /* ------------------------------------------------------------ progress */

  levelRecord(level: number): LevelRecord {
    return this.activeProfile()?.levels[level] ?? emptyLevelRecord();
  }

  isLevelUnlocked(level: number): boolean {
    if (level <= 1) return true;
    return this.levelRecord(level - 1).completed;
  }

  /** Highest level the player may enter. */
  highestUnlockedLevel(maxLevel: number): number {
    let highest = 1;
    for (let level = 2; level <= maxLevel; level += 1) {
      if (!this.isLevelUnlocked(level)) break;
      highest = level;
    }
    return highest;
  }

  totalStars(): number {
    const profile = this.activeProfile();
    if (profile === null) return 0;
    return Object.values(profile.levels).reduce((sum, record) => sum + record.bestStars, 0);
  }

  /** Stars earned in Tide Run, kept apart from the level stars. */
  tideStars(): number {
    const profile = this.activeProfile();
    return profile === null ? 0 : tideStarsOf(profile);
  }

  /** What animal unlocks count: level stars and tide stars together. */
  unlockStars(): number {
    return this.totalStars() + this.tideStars();
  }

  isLevelComplete(level: number): boolean {
    return this.levelRecord(level).completed;
  }

  levelsCompleted(): number {
    const profile = this.activeProfile();
    if (profile === null) return 0;
    return Object.values(profile.levels).filter((record) => record.completed).length;
  }

  /**
   * Fold a finished run into the save. Bests only ever improve, so replaying a
   * level badly cannot erase a good result.
   */
  recordRun(stats: GameRunStats, now: Date = new Date()): RecordRunResult {
    const profile = this.activeProfile();
    if (profile === null) {
      return { newUnlocks: [], previousBestScore: 0, improved: false };
    }

    const before = profile.levels[stats.level] ?? emptyLevelRecord();
    const previousBestScore = before.bestScore;
    const timeSec = stats.elapsedMs / 1000;

    const after: LevelRecord = {
      ...before,
      completed: before.completed || stats.reachedGoal,
      bestStars: Math.max(before.bestStars, stats.stars),
      bestScore: Math.max(before.bestScore, stats.score),
      bestWpm: Math.max(before.bestWpm, stats.wpm),
      bestAccuracy: Math.max(before.bestAccuracy, stats.accuracy),
      // Fastest, not largest - and only a completed run sets a time.
      bestTimeSec: stats.reachedGoal
        ? before.bestTimeSec === 0
          ? timeSec
          : Math.min(before.bestTimeSec, timeSec)
        : before.bestTimeSec,
    };
    profile.levels[stats.level] = after;

    profile.totals.score += stats.score;
    profile.totals.wordsTyped += stats.wordsCorrect;
    profile.totals.playMs += stats.elapsedMs;
    profile.totals.levelsCompleted = Object.values(profile.levels).filter(
      (r) => r.completed,
    ).length;
    profile.totals.stars = Object.values(profile.levels).reduce((sum, r) => sum + r.bestStars, 0);

    // History and miss counts feed the grown-up screen. Recorded for every
    // finished run, good or bad: a chart of bests only would never show a dip.
    profile.history.push({
      at: now.toISOString(),
      level: stats.level,
      wpm: Math.round(stats.wpm * 10) / 10,
      accuracy: Math.round(stats.accuracy * 10) / 10,
      stars: stats.stars,
    });
    if (profile.history.length > RUN_HISTORY_LIMIT) {
      profile.history.splice(0, profile.history.length - RUN_HISTORY_LIMIT);
    }
    for (const word of stats.missedWords) {
      profile.missedWords[word] = (profile.missedWords[word] ?? 0) + 1;
    }
    trimCounts(profile.missedWords, MISSED_WORDS_LIMIT);
    for (const [key, count] of Object.entries(stats.missedLetters)) {
      if (count > 0) profile.missedKeys[key] = (profile.missedKeys[key] ?? 0) + count;
    }

    const newUnlocks = this.refreshAnimalUnlocks(profile);

    this.save();
    return {
      newUnlocks,
      previousBestScore,
      improved: after.bestScore > previousBestScore,
    };
  }

  /** Unlock whatever the level and star totals now allow; returns the new ones. */
  private refreshAnimalUnlocks(profile: ProfileSlot): AnimalId[] {
    const known = new Set(profile.unlockedAnimals);
    const stars = profile.totals.stars + tideStarsOf(profile);
    const unlocked = unlockedAnimalsFor(profile.totals.levelsCompleted, stars);
    profile.unlockedAnimals = unlocked;
    return unlocked.filter((id) => !known.has(id));
  }

  /**
   * Grant every cosmetic the profile has earned and doesn't have yet.
   * `clearedThisRun` lets a run in progress count before it has ended.
   */
  private grantCosmetics(profile: ProfileSlot, clearedThisRun = 0): CosmeticId[] {
    const known = new Set(profile.unlockedCosmetics);
    const earned = earnedCosmetics({
      mostLevelsInRun: Math.max(profile.tide.mostLevelsInRun, clearedThisRun),
      tideStars: tideStarsOf(profile),
    });
    const fresh = earned.filter((id) => !known.has(id));
    profile.unlockedCosmetics = [...profile.unlockedCosmetics, ...fresh];
    return fresh;
  }

  /**
   * Catch up on anything already earned but never granted: saves from before
   * rewards were granted mid-run, whose runs ended through the pause menu.
   */
  refreshTideRewards(): void {
    const profile = this.activeProfile();
    if (profile === null) return;
    const cosmetics = this.grantCosmetics(profile);
    const animals = this.refreshAnimalUnlocks(profile);
    if (cosmetics.length > 0 || animals.length > 0) this.save();
  }

  /**
   * One level cleared in a Tide Run: the fastest time and the most tide stars
   * are kept, and any reward it earns is granted on the spot. Saved straight
   * away, so a run cut short keeps everything it earned.
   */
  recordTideClear(
    level: number,
    timeSec: number,
    stars: number,
    clearedThisRun: number,
  ): TideClearResult {
    const profile = this.activeProfile();
    const before = profile?.levels[level] ?? emptyLevelRecord();
    const newBest = before.tideBestTimeSec === 0 || timeSec < before.tideBestTimeSec;
    const clear = { level, timeSec, stars, newBest };
    if (profile === null) return { clear, newCosmetics: [], newUnlocks: [] };
    profile.levels[level] = {
      ...before,
      tideBestTimeSec: newBest ? timeSec : before.tideBestTimeSec,
      tideStars: Math.max(before.tideStars, stars),
    };
    const newCosmetics = this.grantCosmetics(profile, clearedThisRun);
    const newUnlocks = this.refreshAnimalUnlocks(profile);
    this.save();
    return { clear, newCosmetics, newUnlocks };
  }

  /** Fold a finished Tide Run into the records, history and unlocks. */
  recordTideRun(
    summary: TideRunSummary,
    typing: TideRunTyping,
    now: Date = new Date(),
  ): TideRunResult {
    const profile = this.activeProfile();
    if (profile === null) {
      return { newRecords: [], newCosmetics: [], newUnlocks: [], tideStarsTotal: 0 };
    }

    const before = profile.tide;
    // A first run has nothing to beat, so it reports no records.
    const beat = (better: boolean): boolean => better && before.runs > 0;
    const newRecords: TideRecordName[] = [];
    if (beat(summary.reachedLevel > before.furthestLevel)) newRecords.push('furthest');
    if (beat(summary.levelsCleared > before.mostLevelsInRun)) newRecords.push('most');
    if (beat(summary.wpm > before.bestRunWpm)) newRecords.push('wpm');
    profile.tide = {
      runs: before.runs + 1,
      furthestLevel: Math.max(before.furthestLevel, summary.reachedLevel),
      mostLevelsInRun: Math.max(before.mostLevelsInRun, summary.levelsCleared),
      bestRunWpm: Math.max(before.bestRunWpm, Math.round(summary.wpm * 10) / 10),
    };

    profile.totals.wordsTyped += typing.wordsCorrect;
    profile.totals.playMs += summary.elapsedMs;
    // A run is real typing, so it joins the history the pace and the grown-up
    // charts are built from. Slow runs count too, or the pace could only rise.
    if (summary.elapsedMs > 0) {
      profile.history.push({
        at: now.toISOString(),
        level: summary.reachedLevel,
        wpm: Math.round(summary.wpm * 10) / 10,
        accuracy: Math.round(summary.accuracy * 10) / 10,
        stars: 0,
      });
      if (profile.history.length > RUN_HISTORY_LIMIT) {
        profile.history.splice(0, profile.history.length - RUN_HISTORY_LIMIT);
      }
    }
    for (const word of typing.missedWords) {
      profile.missedWords[word] = (profile.missedWords[word] ?? 0) + 1;
    }
    trimCounts(profile.missedWords, MISSED_WORDS_LIMIT);
    for (const [key, count] of Object.entries(typing.missedLetters)) {
      if (count > 0) profile.missedKeys[key] = (profile.missedKeys[key] ?? 0) + count;
    }

    const newCosmetics = this.grantCosmetics(profile);
    const newUnlocks = this.refreshAnimalUnlocks(profile);
    this.save();
    return { newRecords, newCosmetics, newUnlocks, tideStarsTotal: tideStarsOf(profile) };
  }

  /** What the Tide Run colours are measured against. */
  tideProgress(): TideProgress {
    return { mostLevelsInRun: this.tideRecords().mostLevelsInRun, tideStars: this.tideStars() };
  }

  setGrade(grade: GradeBand | null): void {
    const profile = this.activeProfile();
    if (profile === null) return;
    profile.grade = grade;
    this.save();
  }

  grade(): GradeBand | null {
    return this.activeProfile()?.grade ?? null;
  }

  /** The helpers page has been shown to the active player. */
  markHelpersSeen(): void {
    const profile = this.activeProfile();
    if (profile === null || profile.seenHelpers) return;
    profile.seenHelpers = true;
    this.save();
  }

  tideRecords(): TideRecords {
    return this.activeProfile()?.tide ?? defaultTideRecords();
  }

  hasCosmetic(id: CosmeticId): boolean {
    return this.activeProfile()?.unlockedCosmetics.includes(id) ?? false;
  }

  /* ------------------------------------------------------------ choices */

  setSelectedAnimal(id: AnimalId): void {
    const profile = this.activeProfile();
    if (profile === null) return;
    profile.selectedAnimal = id;
    this.save();
  }

  setAnimalColor(id: AnimalId, swatchIndex: number | null): void {
    const profile = this.activeProfile();
    if (profile === null) return;
    if (swatchIndex === null) delete profile.colorPerAnimal[id];
    else profile.colorPerAnimal[id] = swatchIndex;
    this.save();
  }

  setSetting<K extends keyof ProfileSettings>(key: K, value: ProfileSettings[K]): void {
    const profile = this.activeProfile();
    if (profile === null) return;
    profile.settings[key] = value;
    this.save();
  }

  settings(): ProfileSettings {
    return this.activeProfile()?.settings ?? defaultSettings();
  }

  /* -------------------------------------------------------- import/export */

  exportCode(): string {
    return encodeSaveCode(this.file);
  }

  /** Validates, then replaces everything. The caller confirms first. */
  importCode(code: string): ImportResult {
    const result = decodeSaveCode(code);
    if (!result.ok) return result;
    this.file = result.file;
    this.save();
    return result;
  }

  /* ---------------------------------------------------------- storage io */

  private read(key: string): string | null {
    if (this.storage === null) {
      this.memoryOnly = true;
      return this.fallback.get(key) ?? null;
    }
    try {
      return this.storage.getItem(key);
    } catch {
      this.memoryOnly = true;
      return this.fallback.get(key) ?? null;
    }
  }

  private write(key: string, value: string): void {
    this.fallback.set(key, value);
    if (this.storage === null) {
      this.memoryOnly = true;
      return;
    }
    try {
      this.storage.setItem(key, value);
    } catch {
      // Private browsing, quota, or a blocked origin: keep playing in memory.
      this.memoryOnly = true;
    }
  }
}

/** Builds a manager around window.localStorage when it is usable. */
export function createSaveManager(): SaveManager {
  try {
    if (typeof localStorage === 'undefined') return new SaveManager(null);
    // Touch it: some browsers throw only on access.
    const probe = '__questjump_probe__';
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return new SaveManager(localStorage);
  } catch {
    return new SaveManager(null);
  }
}
